import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { proyectoService, trabajadorService, capatazService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import ImageUploader from '../components/ImageUploader';
import GastosProyecto from '../components/GastosProyecto';
import '../styles/ProyectoDetalle.css';

const ProyectoDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { esContratista, esCapataz } = useAuth();
  const [proyecto, setProyecto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trabajadoresDisponibles, setTrabajadoresDisponibles] = useState([]);
  const [capatacesDisponibles, setCapatacesDisponibles] = useState([]);
  const [modalAsignar, setModalAsignar] = useState(false);
  const [modalItem, setModalItem] = useState(false);
  const [tipoAsignacion, setTipoAsignacion] = useState(''); // 'trabajador' o 'capataz'
  const [modoEdicion, setModoEdicion] = useState(false);
  const [formAsignacion, setFormAsignacion] = useState({
    trabajador_id: '',
    capataz_id: '',
    rol: '',
    servicio_id: null
  });
  const [formItem, setFormItem] = useState({
    tipo: 'servicio',
    servicio_id: '',
    producto_id: '',
    descripcion: '',
    cantidad: 1,
    precio_unitario: 0
  });
  const [serviciosDisponibles, setServiciosDisponibles] = useState([]);
  const [productosDisponibles, setProductosDisponibles] = useState([]);
  const [formEdicion, setFormEdicion] = useState({
    fecha_fin_estimada: '',
    observaciones: ''
  });

  useEffect(() => {
    cargarProyecto();
    if (esContratista) {
      cargarTrabajadores();
      cargarCapataces();
    }
  }, [id]);

  const cargarProyecto = async () => {
    try {
      const response = await proyectoService.getById(id);
      setProyecto(response.data);
    } catch (error) {
      console.error('Error al cargar proyecto:', error);
      alert('Error al cargar proyecto');
      navigate('/proyectos');
    } finally {
      setLoading(false);
    }
  };

  const cargarTrabajadores = async () => {
    try {
      const response = await trabajadorService.getAll({ activo: true });
      setTrabajadoresDisponibles(response.data);
    } catch (error) {
      console.error('Error al cargar trabajadores:', error);
    }
  };

  const cargarCapataces = async () => {
    try {
      const response = await capatazService.getLista();
      setCapatacesDisponibles(response.data.filter(c => c.estado_asociacion === 'aceptado'));
    } catch (error) {
      console.error('Error al cargar capataces:', error);
    }
  };

  const abrirModalAsignar = (tipo, servicio_id = null) => {
    setTipoAsignacion(tipo);
    setFormAsignacion({
      trabajador_id: '',
      capataz_id: '',
      rol: '',
      servicio_id
    });
    setModalAsignar(true);
  };

  const cerrarModalAsignar = () => {
    setModalAsignar(false);
    setTipoAsignacion('');
  };

  const handleAsignar = async () => {
    try {
      if (tipoAsignacion === 'trabajador') {
        if (!formAsignacion.trabajador_id) {
          alert('Selecciona un trabajador');
          return;
        }
        await proyectoService.asignarTrabajador({
          proyecto_id: id,
          trabajador_id: formAsignacion.trabajador_id,
          rol: formAsignacion.rol,
          servicio_id: formAsignacion.servicio_id
        });
      } else if (tipoAsignacion === 'capataz') {
        if (!formAsignacion.capataz_id) {
          alert('Selecciona un capataz');
          return;
        }
        await proyectoService.asignarCapataz({
          proyecto_id: id,
          capataz_id: formAsignacion.capataz_id
        });
      }

      cargarProyecto();
      cerrarModalAsignar();
      alert(`${tipoAsignacion === 'trabajador' ? 'Trabajador' : 'Capataz'} asignado exitosamente`);
    } catch (error) {
      console.error('Error al asignar:', error);
      alert(error.response?.data?.error || 'Error al asignar');
    }
  };

  const handleDesasignarTrabajador = async (asignacion_id) => {
    if (!window.confirm('¿Desasignar este trabajador del proyecto?')) return;

    try {
      await proyectoService.desasignarTrabajador(asignacion_id);
      cargarProyecto();
      alert('Trabajador desasignado');
    } catch (error) {
      console.error('Error al desasignar:', error);
      alert('Error al desasignar trabajador');
    }
  };

  const desasignarCapataz = async () => {
    if (!window.confirm('¿Desasignar el capataz de este proyecto?')) return;

    try {
      await proyectoService.desasignarCapataz({ proyecto_id: proyecto.id });
      cargarProyecto();
      alert('Capataz desasignado exitosamente');
    } catch (error) {
      console.error('Error al desasignar capataz:', error);
      alert(error.response?.data?.error || 'Error al desasignar capataz');
    }
  };

  const handleImagenSubida = async (url) => {
    try {
      await proyectoService.agregarImagen({
        proyecto_id: id,
        url,
        descripcion: `Imagen del proyecto ${proyecto.nombre}`
      });
      cargarProyecto();
    } catch (error) {
      console.error('Error al agregar imagen:', error);
      alert('Error al agregar imagen');
    }
  };

  const handleEliminarImagen = async (imagen_id) => {
    if (!window.confirm('¿Eliminar esta imagen?')) return;

    try {
      await proyectoService.eliminarImagen(imagen_id);
      cargarProyecto();
    } catch (error) {
      console.error('Error al eliminar imagen:', error);
      alert('Error al eliminar imagen');
    }
  };

  const habilitarEdicion = () => {
    setFormEdicion({
      fecha_fin_estimada: proyecto.fecha_fin_estimada || '',
      observaciones: proyecto.observaciones || ''
    });
    setModoEdicion(true);
  };

  const cancelarEdicion = () => {
    setModoEdicion(false);
  };

  const guardarEdicion = async () => {
    try {
      await proyectoService.update(id, formEdicion);
      setModoEdicion(false);
      cargarProyecto();
      alert('Proyecto actualizado exitosamente');
    } catch (error) {
      console.error('Error al actualizar proyecto:', error);
      alert('Error al actualizar proyecto');
    }
  };

  const abrirModalItem = () => {
    setFormItem({
      tipo: 'servicio',
      servicio_id: '',
      producto_id: '',
      descripcion: '',
      cantidad: 1,
      precio_unitario: 0
    });
    setModalItem(true);
  };

  const cerrarModalItem = () => {
    setModalItem(false);
  };

  const handleAgregarItem = async () => {
    try {
      if (!formItem.descripcion || formItem.cantidad <= 0 || formItem.precio_unitario < 0) {
        alert('Complete todos los campos correctamente');
        return;
      }

      await proyectoService.agregarItem({
        proyecto_id: id,
        tipo: formItem.tipo,
        servicio_id: null,
        producto_id: null,
        descripcion: formItem.descripcion,
        cantidad: formItem.cantidad,
        precio_unitario: formItem.precio_unitario
      });

      cargarProyecto();
      cerrarModalItem();
      alert(`${formItem.tipo === 'servicio' ? 'Servicio' : 'Producto'} agregado exitosamente`);
    } catch (error) {
      console.error('Error al agregar item:', error);
      alert(error.response?.data?.error || 'Error al agregar item');
    }
  };

  const handleEliminarItem = async (item_id) => {
    if (!window.confirm('¿Eliminar este item?')) return;

    try {
      await proyectoService.eliminarItem(item_id);
      cargarProyecto();
      alert('Item eliminado exitosamente');
    } catch (error) {
      console.error('Error al eliminar item:', error);
      alert('Error al eliminar item');
    }
  };

  const handleFinalizarProyecto = async () => {
    if (!window.confirm('¿Finalizar este proyecto? Esta acción cambiará el estado a COMPLETADO y requiere que el avance esté al 100%.')) return;

    try {
      await proyectoService.finalizar(id);
      cargarProyecto();
      alert('Proyecto finalizado exitosamente');
    } catch (error) {
      console.error('Error al finalizar proyecto:', error);
      alert(error.response?.data?.error || 'Error al finalizar proyecto');
    }
  };

  if (loading) {
    return <div className="loading">Cargando proyecto...</div>;
  }

  if (!proyecto) {
    return <div className="loading">Proyecto no encontrado</div>;
  }

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
  //const BASE_URL = API_URL.replace('/api', '');
  const proyectoCompletado = proyecto.estado === 'completado';

  return (
    <div className="proyecto-detalle-container">
      <div className="detalle-header">
        <button className="btn-volver" onClick={() => navigate('/proyectos')}>
          ← Volver
        </button>
        <h1>{proyecto.nombre}</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {esContratista && proyecto.estado !== 'completado' && (
            <button className="btn btn-success" onClick={handleFinalizarProyecto}>
              ✓ Finalizar Proyecto
            </button>
          )}
          <span className={`estado-badge ${proyecto.estado}`}>
            {proyecto.estado.replace('_', ' ')}
          </span>
        </div>
      </div>

      <div className="detalle-grid">
        {/* Información General */}
        <div className="detalle-card">
          <div className="card-header">
            <h2>Información General</h2>
            {esContratista && !modoEdicion && !proyectoCompletado && (
              <button className="btn btn-sm btn-secondary" onClick={habilitarEdicion}>
                ✏️ Editar
              </button>
            )}
            {esContratista && modoEdicion && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="btn btn-sm btn-success" onClick={guardarEdicion}>
                  ✓ Guardar
                </button>
                <button className="btn btn-sm btn-secondary" onClick={cancelarEdicion}>
                  ✕ Cancelar
                </button>
              </div>
            )}
            {proyectoCompletado && (
              <span style={{ color: '#27ae60', fontWeight: 'bold' }}>✓ Proyecto Completado</span>
            )}
          </div>
          <div className="info-grid">
            <div className="info-item">
              <strong>Número:</strong>
              <span>{proyecto.numero_proyecto}</span>
            </div>
            <div className="info-item">
              <strong>Cliente:</strong>
              <span>{proyecto.cliente_nombre}</span>
            </div>
            {proyecto.direccion && (
              <div className="info-item">
                <strong>Dirección:</strong>
                <span>{proyecto.direccion}</span>
              </div>
            )}
            {proyecto.presupuesto_asignado && (
              <div className="info-item">
                <strong>Presupuesto:</strong>
                <span>${parseFloat(proyecto.presupuesto_asignado).toLocaleString()}</span>
              </div>
            )}
            {proyecto.fecha_inicio && (
              <div className="info-item">
                <strong>Fecha Inicio:</strong>
                <span>{new Date(proyecto.fecha_inicio).toLocaleDateString()}</span>
              </div>
            )}
            <div className="info-item">
              <strong>Fecha Fin Estimada:</strong>
              {modoEdicion ? (
                <input
                  type="date"
                  value={formEdicion.fecha_fin_estimada}
                  onChange={(e) => setFormEdicion({ ...formEdicion, fecha_fin_estimada: e.target.value })}
                  style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #ddd' }}
                />
              ) : (
                <span>{proyecto.fecha_fin_estimada ? new Date(proyecto.fecha_fin_estimada).toLocaleDateString() : 'No especificada'}</span>
              )}
            </div>
          </div>

          {(proyecto.observaciones || modoEdicion) && (
            <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '2px solid #f0f0f0' }}>
              <strong>Observaciones:</strong>
              {modoEdicion ? (
                <textarea
                  value={formEdicion.observaciones}
                  onChange={(e) => setFormEdicion({ ...formEdicion, observaciones: e.target.value })}
                  rows="3"
                  style={{ width: '100%', marginTop: '8px', padding: '8px', borderRadius: '6px', border: '1px solid #ddd' }}
                  placeholder="Observaciones del proyecto..."
                />
              ) : (
                <p style={{ margin: '8px 0 0 0', color: '#666', lineHeight: '1.5' }}>{proyecto.observaciones || 'Sin observaciones'}</p>
              )}
            </div>
          )}

          <div className="proyecto-progreso">
            <div className="progreso-header">
              <span>Avance General</span>
              <span className="progreso-porcentaje">{proyecto.porcentaje_avance || 0}%</span>
            </div>
            <div className="progreso-bar">
              <div
                className="progreso-fill"
                style={{ width: `${proyecto.porcentaje_avance || 0}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Capataz */}
        <div className="detalle-card">
          <div className="card-header">
            <h2>Capataz Asignado</h2>
            {esContratista && !proyecto.capataz_nombre && !proyectoCompletado && (
              <button className="btn btn-sm btn-primary" onClick={() => abrirModalAsignar('capataz')}>
                + Asignar
              </button>
            )}
          </div>

          {proyecto.capataz_nombre ? (
            <div className="asignado-info">
              <div>
                <p><strong>Nombre:</strong> {proyecto.capataz_nombre}</p>
                {proyecto.capataz_telefono && (
                  <p><strong>Teléfono:</strong> {proyecto.capataz_telefono}</p>
                )}
              </div>
              {esContratista && !proyectoCompletado && (
                <button
                  className="btn btn-sm btn-danger"
                  onClick={desasignarCapataz}
                  title="Desasignar capataz"
                >
                  Desasignar
                </button>
              )}
            </div>
          ) : (
            <p className="empty-text">Sin capataz asignado</p>
          )}
        </div>

        {/* Trabajadores Asignados */}
        <div className="detalle-card trabajadores-card">
          <div className="card-header">
            <h2>Trabajadores</h2>
            {esContratista && !proyectoCompletado && (
              <button className="btn btn-sm btn-primary" onClick={() => abrirModalAsignar('trabajador')}>
                + Asignar
              </button>
            )}
          </div>

          {proyecto.trabajadores && proyecto.trabajadores.length > 0 ? (
            <div className="trabajadores-lista">
              {proyecto.trabajadores.map(asignacion => (
                <div key={asignacion.id} className="trabajador-item">
                  <div>
                    <strong>{asignacion.nombre}</strong>
                    {asignacion.especialidad && <span className="especialidad-tag">{asignacion.especialidad}</span>}
                    {asignacion.rol && <p className="rol">Rol: {asignacion.rol}</p>}
                  </div>
                  {esContratista && !proyectoCompletado && (
                    <button
                      className="btn-icon-delete"
                      onClick={() => handleDesasignarTrabajador(asignacion.id)}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-text">Sin trabajadores asignados</p>
          )}
        </div>

        {/* Servicios del Proyecto */}
        {((proyecto.servicios && proyecto.servicios.length > 0) || (!proyecto.cotizacion_id && esContratista)) && (
          <div className="detalle-card servicios-card">
            <div className="card-header">
              <h2>Servicios y Trabajadores Asignados</h2>
              {!proyecto.cotizacion_id && esContratista && !proyectoCompletado && (
                <button className="btn btn-sm btn-primary" onClick={abrirModalItem}>
                  + Agregar Servicio/Producto
                </button>
              )}
            </div>
            <div className="servicios-lista">
              {proyecto.servicios.map(servicio => {
                // Filtrar trabajadores asignados a este servicio
                const trabajadoresDelServicio = proyecto.trabajadores?.filter(
                  t => t.servicio_id === servicio.servicio_id
                ) || [];

                return (
                  <div key={servicio.id} className="servicio-item-expandido">
                    <div className="servicio-header">
                      <div className="servicio-info">
                        <strong>{servicio.nombre}</strong>
                        {servicio.descripcion && <p className="descripcion">{servicio.descripcion}</p>}
                        <div className="servicio-detalles">
                          <span>Cantidad: {servicio.cantidad} {servicio.unidad}</span>
                          <span>Precio: ${parseFloat(servicio.precio_unitario).toLocaleString()}</span>
                          <span className="subtotal">Subtotal: ${parseFloat(servicio.subtotal).toLocaleString()}</span>
                        </div>
                      </div>
                      {esContratista && !proyectoCompletado && (
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => abrirModalAsignar('trabajador', servicio.servicio_id)}
                          >
                            + Asignar Trabajador
                          </button>
                          {!proyecto.cotizacion_id && (
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleEliminarItem(servicio.id)}
                            >
                              🗑️ Eliminar
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Trabajadores asignados a este servicio */}
                    {trabajadoresDelServicio.length > 0 && (
                      <div className="trabajadores-servicio">
                        <p className="trabajadores-titulo">Trabajadores asignados:</p>
                        <div className="trabajadores-lista-mini">
                          {trabajadoresDelServicio.map(trabajador => (
                            <div key={trabajador.id} className="trabajador-mini">
                              <span>{trabajador.nombre}</span>
                              {trabajador.especialidad && <span className="especialidad-tag">{trabajador.especialidad}</span>}
                              {esContratista && !proyectoCompletado && (
                                <button
                                  className="btn-icon-delete-mini"
                                  onClick={() => handleDesasignarTrabajador(trabajador.id)}
                                  title="Desasignar trabajador"
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Trabajadores sin servicio asignado (asignados al proyecto en general) */}
            {(() => {
              const trabajadoresSinServicio = proyecto.trabajadores?.filter(t => !t.servicio_id) || [];
              if (trabajadoresSinServicio.length > 0) {
                return (
                  <div className="trabajadores-generales">
                    <h3>Trabajadores del proyecto (sin servicio específico)</h3>
                    <div className="trabajadores-lista-mini">
                      {trabajadoresSinServicio.map(trabajador => (
                        <div key={trabajador.id} className="trabajador-mini">
                          <span>{trabajador.nombre}</span>
                          {trabajador.especialidad && <span className="especialidad-tag">{trabajador.especialidad}</span>}
                          {trabajador.rol && <span className="rol-mini">({trabajador.rol})</span>}
                          {esContratista && !proyectoCompletado && (
                            <button
                              className="btn-icon-delete-mini"
                              onClick={() => handleDesasignarTrabajador(trabajador.id)}
                              title="Desasignar trabajador"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }
            })()}
          </div>
        )}

        {/* Productos del Proyecto */}
        {proyecto.productos && proyecto.productos.length > 0 && (
          <div className="detalle-card productos-card">
            <h2>Productos/Materiales</h2>
            <div className="productos-lista">
              {proyecto.productos.map(producto => (
                <div key={producto.id} className="producto-item">
                  <div className="producto-info">
                    <strong>{producto.nombre}</strong>
                    {producto.descripcion && <p className="descripcion">{producto.descripcion}</p>}
                    <div className="producto-detalles">
                      <span>Cantidad: {producto.cantidad} {producto.unidad}</span>
                      <span>Precio: ${parseFloat(producto.precio_unitario).toLocaleString()}</span>
                      <span className="subtotal">Subtotal: ${parseFloat(producto.subtotal).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Imágenes del Proyecto */}
        <div className="detalle-card imagenes-card">
          <h2>Galería de Imágenes</h2>

          {!proyectoCompletado && (
            <ImageUploader
              onImageUploaded={handleImagenSubida}
              multiple={false}
            />
          )}

          {proyecto.imagenes && proyecto.imagenes.length > 0 ? (
            <div className="imagenes-galeria">
              {proyecto.imagenes.map(imagen => (
                <div key={imagen.id} className="imagen-item">
                  <img
                    //src={`${BASE_URL}${imagen.url}`}
		    src={imagen.url.startsWith('http') ? imagen.url : `${window.location.origin}${imagen.url}`}
                    alt={imagen.descripcion || 'Imagen del proyecto'}
                  />
                  {esContratista && !proyectoCompletado && (
                    <button
                      className="btn-eliminar-imagen"
                      onClick={() => handleEliminarImagen(imagen.id)}
                    >
                      🗑️
                    </button>
                  )}
                  {imagen.descripcion && <p>{imagen.descripcion}</p>}
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-text">Sin imágenes</p>
          )}
        </div>

        {/* Gastos del Proyecto */}
        {esContratista && (
          <GastosProyecto proyecto_id={id} proyectoCompletado={proyectoCompletado} />
        )}

        {/* Últimos Avances */}
        <div className="detalle-card avances-card">
          <h2>Últimos Avances</h2>

          {proyecto.avances && proyecto.avances.length > 0 ? (
            <div className="avances-lista">
              {proyecto.avances.map(avance => (
                <div key={avance.id} className="avance-item">
                  <div className="avance-header">
                    <strong>{new Date(avance.fecha).toLocaleDateString()}</strong>
                    <span className="avance-porcentaje">{avance.porcentaje_avance}%</span>
                  </div>
                  <p className="avance-descripcion">{avance.descripcion}</p>
                  {avance.observaciones && (
                    <p className="avance-observaciones"><em>{avance.observaciones}</em></p>
                  )}
                  {avance.incidencias && (
                    <p className="avance-incidencias">⚠️ {avance.incidencias}</p>
                  )}

                  {/* Imágenes del avance */}
                  {(() => {
                    try {
                      // Parsear imagenes si es string, o usar directamente si ya es array
                      let imagenes = [];
                      if (avance.imagenes) {
                        if (typeof avance.imagenes === 'string') {
                          const parsed = JSON.parse(avance.imagenes);
                          imagenes = Array.isArray(parsed) ? parsed : [];
                        } else if (Array.isArray(avance.imagenes)) {
                          imagenes = avance.imagenes;
                        }
                      }

                      if (imagenes.length > 0) {
                        return (
                          <div className="avance-imagenes">
                            <p className="avance-imagenes-titulo">📷 Fotos del avance:</p>
                            <div className="avance-imagenes-grid">
                              {imagenes.map((img, idx) => {
                                // Manejar formato antiguo: {url: {url: "...", filename: "..."}}
                                // y formato nuevo: {url: "..."}
                                const imageUrl = typeof img.url === 'string' ? img.url : img.url?.url || '';
                                if (!imageUrl) return null;

                                return (
                                  <img
                                    key={idx}
                                    //src={`${BASE_URL}${imageUrl}`}
				    src={imageUrl.startsWith('http') ? imageUrl : `${window.location.origin}${imageUrl}`}
                                    alt={img.descripcion || `Avance ${idx + 1}`}
                                    className="avance-imagen"
                                  />
                                );
                              })}
                            </div>
                          </div>
                        );
                      }
                    } catch (error) {
                      console.error('Error al parsear imágenes del avance:', error);
                    }
                    return null;
                  })()}

                  <p className="avance-capataz">Por: {avance.capataz_nombre}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-text">Sin avances registrados</p>
          )}
        </div>
      </div>

      {/* Modal Asignar */}
      {modalAsignar && (
        <div className="modal-overlay" onClick={cerrarModalAsignar}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Asignar {tipoAsignacion === 'trabajador' ? 'Trabajador' : 'Capataz'}</h2>

            {tipoAsignacion === 'trabajador' ? (
              <>
                <div className="form-group">
                  <label>Trabajador *</label>
                  <select
                    value={formAsignacion.trabajador_id}
                    onChange={(e) => setFormAsignacion({ ...formAsignacion, trabajador_id: e.target.value })}
                  >
                    <option value="">Seleccionar trabajador</option>
                    {trabajadoresDisponibles.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.nombre} {t.especialidad && `- ${t.especialidad}`}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Rol en el Proyecto</label>
                  <input
                    type="text"
                    value={formAsignacion.rol}
                    onChange={(e) => setFormAsignacion({ ...formAsignacion, rol: e.target.value })}
                    placeholder="Ej: Maestro de obra, Ayudante"
                  />
                </div>
              </>
            ) : (
              <div className="form-group">
                <label>Capataz *</label>
                <select
                  value={formAsignacion.capataz_id}
                  onChange={(e) => setFormAsignacion({ ...formAsignacion, capataz_id: e.target.value })}
                >
                  <option value="">Seleccionar capataz</option>
                  {capatacesDisponibles.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.nombre} {c.especialidad && `- ${c.especialidad}`}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={cerrarModalAsignar}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleAsignar}>
                Asignar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Agregar Servicio/Producto */}
      {modalItem && (
        <div className="modal-overlay" onClick={cerrarModalItem}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Agregar Servicio/Producto</h2>

            <div className="form-group">
              <label>Tipo *</label>
              <select
                value={formItem.tipo}
                onChange={(e) => setFormItem({ ...formItem, tipo: e.target.value })}
              >
                <option value="servicio">Servicio</option>
                <option value="producto">Producto</option>
              </select>
            </div>

            <div className="form-group">
              <label>Descripción *</label>
              <textarea
                value={formItem.descripcion}
                onChange={(e) => setFormItem({ ...formItem, descripcion: e.target.value })}
                rows="3"
                placeholder="Descripción del servicio o producto"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Cantidad *</label>
                <input
                  type="number"
                  value={formItem.cantidad}
                  onChange={(e) => setFormItem({ ...formItem, cantidad: parseFloat(e.target.value) || 0 })}
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="form-group">
                <label>Precio Unitario *</label>
                <input
                  type="number"
                  value={formItem.precio_unitario}
                  onChange={(e) => setFormItem({ ...formItem, precio_unitario: parseFloat(e.target.value) || 0 })}
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={cerrarModalItem}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleAgregarItem}>
                Agregar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProyectoDetalle;
